/**
 * @file UnityPlayer — embeds the Sky City Rush WebGL build directly (no iframe)
 * by loading Unity's loader script and calling createUnityInstance against a
 * canvas we own. Handles a start gate, load progress, a loading overlay,
 * fullscreen and teardown.
 *
 * The build only begins downloading/executing AFTER the user presses Play
 * (unless `autoStart` is set) — the 200 MB+ build must never boot on render.
 *
 * The build is served straight from /public and is never modified or moved.
 *
 * Future compatibility: the component already accepts a `manifestUrl` prop. It
 * is intentionally unused for now — once the Unity build reads its competition
 * manifest dynamically, this is where the URL will be forwarded (e.g. passed as
 * a Unity `arguments` entry or via SendMessage after boot). Keeping it in the
 * public API now means callers won't have to change later.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

/**
 * Location + entry files of the uploaded build, relative to the public root.
 * Values mirror the build's own index.html config exactly.
 * @typedef {Object} UnityBuildConfig
 * @property {string} basePath
 * @property {string} loaderFile
 * @property {string} dataFile
 * @property {string} frameworkFile
 * @property {string} codeFile
 * @property {string} streamingAssetsDir
 * @property {string} companyName
 * @property {string} productName
 * @property {string} productVersion
 */

/** @type {UnityBuildConfig} */
export const SKY_CITY_RUSH_BUILD = {
  basePath: '/sky-city-rush-weekly/sky-city-rush',
  loaderFile: 'Build/sky-city-rush.loader.js',
  dataFile: 'Build/sky-city-rush.data',
  frameworkFile: 'Build/sky-city-rush.framework.js',
  codeFile: 'Build/sky-city-rush.wasm',
  streamingAssetsDir: 'StreamingAssets',
  companyName: 'Loen Interactive',
  productName: 'Sky City Rush',
  productVersion: '1',
};

/**
 * @typedef {Object} UnityPlayerHandle
 * @property {() => void} start          Begin loading/executing the build.
 * @property {() => void} enterFullscreen
 * @property {boolean} isStarted
 */

/**
 * @param {{
 *   manifestUrl?: string,
 *   build?: UnityBuildConfig,
 *   aspectRatio?: string,
 *   autoStart?: boolean,
 *   posterLabel?: string,
 *   onStart?: () => void,
 *   onReady?: (instance: any) => void,
 *   className?: string
 * }} props
 * @param {React.Ref<UnityPlayerHandle>} ref
 */
export const UnityPlayer = forwardRef(function UnityPlayer(
  {
    manifestUrl, // reserved for future manifest-driven builds; intentionally unused
    build = SKY_CITY_RUSH_BUILD,
    aspectRatio = '16 / 10',
    autoStart = false,
    posterLabel = 'Start this week’s challenge',
    onStart,
    onReady,
    className = '',
  },
  ref
) {
  const canvasRef = useRef(/** @type {HTMLCanvasElement|null} */ (null));
  const instanceRef = useRef(/** @type {any} */ (null));
  const [started, setStarted] = useState(Boolean(autoStart));
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState(
    /** @type {'loading'|'ready'|'error'} */ ('loading')
  );
  const [errorMessage, setErrorMessage] = useState('');

  const start = useCallback(() => {
    setStarted((prev) => {
      if (!prev && onStart) onStart();
      return true;
    });
  }, [onStart]);

  const enterFullscreen = useCallback(() => {
    const instance = instanceRef.current;
    if (instance && typeof instance.SetFullscreen === 'function') {
      instance.SetFullscreen(1);
    }
  }, []);

  useImperativeHandle(
    ref,
    () => ({ start, enterFullscreen, isStarted: started }),
    [start, enterFullscreen, started]
  );

  useEffect(() => {
    if (!started) return undefined;

    let cancelled = false;
    const base = build.basePath.replace(/\/$/, '');
    const loaderUrl = `${base}/${build.loaderFile}`;

    const config = {
      dataUrl: `${base}/${build.dataFile}`,
      frameworkUrl: `${base}/${build.frameworkFile}`,
      codeUrl: `${base}/${build.codeFile}`,
      streamingAssetsUrl: `${base}/${build.streamingAssetsDir}`,
      companyName: build.companyName,
      productName: build.productName,
      productVersion: build.productVersion,
    };

    function boot() {
      if (cancelled || !canvasRef.current) return;
      const create = /** @type {any} */ (window).createUnityInstance;
      if (typeof create !== 'function') {
        setStatus('error');
        setErrorMessage('Unity loader did not initialise.');
        return;
      }
      create(canvasRef.current, config, (p) => {
        if (!cancelled) setProgress(p);
      })
        .then((instance) => {
          if (cancelled) {
            if (instance && instance.Quit) instance.Quit();
            return;
          }
          instanceRef.current = instance;
          setProgress(1);
          setStatus('ready');
          if (onReady) onReady(instance);
        })
        .catch((err) => {
          if (cancelled) return;
          setStatus('error');
          setErrorMessage((err && err.message) || 'Failed to start the game.');
        });
    }

    setStatus('loading');
    setProgress(0);

    if (typeof (/** @type {any} */ (window).createUnityInstance) === 'function') {
      boot();
    } else {
      const existing = document.querySelector(
        `script[data-unity-loader="${loaderUrl}"]`
      );
      if (existing) {
        existing.addEventListener('load', boot, { once: true });
      } else {
        const injected = document.createElement('script');
        injected.src = loaderUrl;
        injected.async = true;
        injected.dataset.unityLoader = loaderUrl;
        injected.onload = boot;
        injected.onerror = () => {
          if (!cancelled) {
            setStatus('error');
            setErrorMessage('Could not download the Unity build.');
          }
        };
        document.body.appendChild(injected);
      }
    }

    return () => {
      cancelled = true;
      const instance = instanceRef.current;
      instanceRef.current = null;
      if (instance && typeof instance.Quit === 'function') {
        try {
          instance.Quit();
        } catch (e) {
          /* ignore teardown errors */
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started, build.basePath]);

  const percent = Math.round(progress * 100);

  return (
    <div className={`scrw-unity ${className}`.trim()} style={{ aspectRatio }}>
      <canvas
        ref={canvasRef}
        id="scrw-unity-canvas"
        className="scrw-unity__canvas"
        tabIndex={-1}
      />

      {!started ? (
        <div className="scrw-unity__poster">
          <button
            type="button"
            className="scrw-unity__play"
            onClick={start}
            aria-label={posterLabel}
          >
            <span className="scrw-unity__play-icon" aria-hidden="true">
              ▶
            </span>
          </button>
          <p className="scrw-unity__poster-label">{posterLabel}</p>
        </div>
      ) : status !== 'ready' ? (
        <div className="scrw-unity__overlay" role="status" aria-live="polite">
          {status === 'error' ? (
            <div className="scrw-unity__error">
              <p className="scrw-unity__error-title">Couldn’t start the game.</p>
              {errorMessage ? (
                <p className="scrw-unity__error-detail">{errorMessage}</p>
              ) : null}
            </div>
          ) : (
            <div className="scrw-unity__loading">
              <span className="scrw-spinner" aria-hidden="true" />
              <p className="scrw-unity__loading-label">
                Loading this week’s challenge… {percent}%
              </p>
              <div className="scrw-unity__bar">
                <div
                  className="scrw-unity__bar-fill"
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
});
